import { useState } from "react";

import { cn } from "@/lib/utils";
import { useKindOptions } from "@/hooks/useKindOptions";
import { leafOptionsFor } from "@/lib/attributeReview/rowDecision";

import { CustomLeafTab } from "./CustomLeafTab";
import { ExistingLeavesTab } from "./ExistingLeavesTab";
import { KindPicker } from "./KindPicker";
import { LeafOfferNote } from "./LeafOfferNote";
import type { LeafOffer, ReviewOption } from "./LeafTabs.types";
import { NewNamesTab } from "./NewNamesTab";
import type { useRowActions, useRowDecision } from "./useRowDecision";

type DecisionTab = "existing" | "new" | "custom";
const TABS: ReadonlyArray<readonly [DecisionTab, string]> = [
  ["existing", "Existing leaves"],
  ["new", "New names"],
  ["custom", "Custom Target"],
];

export function LeafDecisionBox({
  mapping,
  decision,
  actions,
  sessionId,
}: {
  mapping: Record<string, unknown>;
  decision: ReturnType<typeof useRowDecision>;
  actions: ReturnType<typeof useRowActions>;
  sessionId: string;
}) {
  const [tab, setTab] = useState<DecisionTab>("existing");
  const kinds = useKindOptions(sessionId);
  const rowKind = (mapping.kind as string | null) ?? null;
  const options = leafOptionsFor({
    rowKind,
    optionsKind: (mapping.leafOptionsKind as string | null) ?? null,
    pickedKind: decision.kind,
    options: (mapping.leafOptions as ReviewOption[] | null) ?? [],
  });
  const folder = decision.kind
    ? (kinds.find((option) => option.kind === decision.kind)?.folder ?? null)
    : ((mapping.folder as string | null) ?? null);
  const offer = (mapping.leafOffer as LeafOffer | null) ?? null;
  return (
    <div className="mb-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
      {offer && (
        <LeafOfferNote
          offer={offer}
          options={options}
          picked={decision.leaf}
          onPick={decision.setLeaf}
        />
      )}
      <div className="grid grid-cols-3 border-b border-slate-200 dark:border-slate-700">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={cn(
              "px-3 py-2 text-sm font-medium",
              tab === key
                ? "bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-100"
                : "text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800",
            )}
          >
            {label}
          </button>
        ))}
      </div>
      <KindPicker
        kinds={kinds}
        kind={
          mapping.needsReview === true
            ? decision.kind
            : (decision.kind ?? rowKind)
        }
        onKind={decision.setKind}
      />
      {tab === "existing" ? (
        <ExistingLeavesTab
          options={options}
          folder={folder}
          enginePick={(mapping.leaf as string | null) ?? null}
          picked={decision.leaf}
          onPick={decision.setLeaf}
        />
      ) : tab === "new" ? (
        <NewNamesTab
          options={options}
          refreshCount={
            (mapping.refreshCount as { leaf?: number } | null)?.leaf ?? 0
          }
          onRefresh={() => actions.refreshNames("leaf")}
          busy={actions.busy}
          picked={decision.leaf}
          onPick={decision.setLeaf}
        />
      ) : (
        <CustomLeafTab
          folder={folder}
          leaf={decision.leaf}
          onLeaf={decision.setLeaf}
        />
      )}
    </div>
  );
}
